export const SESSION_GAP_MS = 30 * 60 * 1000
export const MAX_EVENTS_PER_CALL = 25
export const ACTIVITY_PING = 'app_active'
// Long enough to recompute the cohorts a validation round re-checks: 14-day windows, the
// month-2 retention check at day 60, and about eight weeks of cohorts behind it.
export const RETENTION_DAYS = 183

const DAY_MS = 24 * 60 * 60 * 1000

const MAX_ID_LENGTH = 128
const MAX_CLIENT_DELAY_MS = 15 * 60 * 1000
// Parallel MCP calls for one user race for the same session record; each round one of them wins.
const MAX_SESSION_WRITE_ATTEMPTS = 8
const RETRY_PAUSE_MS = 25

const oneOf = (...values) => (value) => (values.includes(value) ? value : undefined)
const identifier = (value) => (typeof value === 'string' && value.length > 0 && value.length <= MAX_ID_LENGTH ? value : undefined)

// Only what no other table records. Edits, data model and scenario work, AI runs and generated
// workflows come from workflow history; invites and members from the team member table.
// Properties not listed for an event are dropped, so a client can never store anything else.
const CLIENT_EVENTS = {
  exported: { workflowId: identifier, target: oneOf('specs') },
  // History cannot tell tour clicks from real work, so the tour window itself is recorded.
  tour_started: { workflowId: identifier },
  tour_ended: { workflowId: identifier, outcome: oneOf('completed', 'cancelled', 'interrupted', 'error') },
  [ACTIVITY_PING]: {}
}

// session_started is not here: only recordProductEvents itself starts a session.
const SERVER_EVENTS = {
  mcp_tool_called: { workflowId: identifier, tool: identifier, client: oneOf('claude_code', 'cursor', 'windsurf', 'other') }
}

const COMMON_PROPERTIES = {
  projectId: identifier,
  context: oneOf('onboarding'),
  tourWorkflowId: identifier
}

const clean = (rules, raw) => Object.entries(rules).reduce((props, [name, rule]) => {
  const value = rule(raw[name])

  return value === undefined ? props : { ...props, [name]: value }
}, {})

// Only the event's age comes from the device (sentAt - clientAt, both on its clock), so a device
// clock that is off does not move the event. Anything implausible gets the arrival time.
const occurredAtFor = (clientAt, sentAt, now) => {
  const age = Date.parse(sentAt) - Date.parse(clientAt)

  return age >= 0 && age <= MAX_CLIENT_DELAY_MS ? now - age : now
}

// Own properties only: a name like "toString" or "constructor" must not find a rule.
const rulesFor = (table, name) => (typeof name === 'string' && Object.hasOwn(table, name) ? table[name] : undefined)

export const sanitizeEvent = (raw, { now, trusted = false }) => {
  const rules = rulesFor(CLIENT_EVENTS, raw?.eventName) ?? (trusted ? rulesFor(SERVER_EVENTS, raw?.eventName) : undefined)

  if (!rules) {
    return null
  }

  return {
    eventName: raw.eventName,
    occurredAt: occurredAtFor(raw.clientAt, raw.sentAt, now),
    ...clean(COMMON_PROPERTIES, raw),
    ...clean(rules, raw)
  }
}

export const startsNewSession = (lastSeenAt, occurredAt) => {
  const lastSeen = Date.parse(lastSeenAt)

  return !Number.isFinite(lastSeen) || occurredAt - lastSeen >= SESSION_GAP_MS
}

export const isoWeek = (millis) => {
  const date = new Date(millis)
  const thursday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 4 - (date.getUTCDay() || 7)))
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1)
  const week = Math.ceil(((thursday - yearStart) / DAY_MS + 1) / 7)

  return `${thursday.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
}

// The secondary index only holds rows that carry its key, so only MCP calls get one.
const indexKeys = (event) => (event.eventName === 'mcp_tool_called' ? { mcpWeek: isoWeek(event.occurredAt) } : {})

const toItem = (userId, event, sessionIndex, makeId) => {
  const id = makeId()
  const occurredAt = new Date(event.occurredAt).toISOString()

  return {
    ...event,
    ...indexKeys(event),
    id,
    userId,
    eventKey: `${occurredAt}#${id}`,
    occurredAt,
    sessionIndex,
    expiresAt: Math.floor((event.occurredAt + RETENTION_DAYS * DAY_MS) / 1000)
  }
}

// store: readSession(userId) -> { lastSeenAt, sessionIndex } | null for an unknown user, and
// commit(userId, { next, previousLastSeenAt } | null, items) -> writes the session move and the
// event rows together, false (and nothing written) when another request moved the session first.
const pauseBriefly = (attempt) => new Promise((resolve) => setTimeout(resolve, Math.random() * RETRY_PAUSE_MS * (attempt + 1)))

export const recordProductEvents = async ({ userId, events, store, now = Date.now(), trusted = false, makeId, pause = pauseBriefly }) => {
  const accepted = (Array.isArray(events) ? events : [])
    .slice(0, MAX_EVENTS_PER_CALL)
    .map((raw) => sanitizeEvent(raw, { now, trusted }))
    .filter(Boolean)
    .sort((left, right) => left.occurredAt - right.occurredAt)

  if (!userId || accepted.length === 0) {
    return { recorded: 0 }
  }

  const first = accepted[0]
  const last = accepted[accepted.length - 1]

  for (let attempt = 0; attempt < MAX_SESSION_WRITE_ATTEMPTS; attempt++) {
    const session = await store.readSession(userId)

    if (!session) {
      return { recorded: 0 }
    }

    const newSession = startsNewSession(session.lastSeenAt, first.occurredAt)
    const sessionIndex = newSession ? (session.sessionIndex || 0) + 1 : (session.sessionIndex || 1)
    const lastSeenAt = new Date(Math.max(last.occurredAt, Date.parse(session.lastSeenAt) || 0)).toISOString()
    // Another call already moved the session past these events: nothing to write, and nothing to race for.
    const unchanged = !newSession && session.sessionIndex && lastSeenAt === session.lastSeenAt

    const sessionStart = newSession
      ? [{ eventName: 'session_started', occurredAt: first.occurredAt, ...clean(COMMON_PROPERTIES, first) }]
      : []
    const items = [...sessionStart, ...accepted.filter((event) => event.eventName !== ACTIVITY_PING)]
      .map((event) => toItem(userId, event, sessionIndex, makeId))
    const sessionChange = unchanged ? null : { next: { lastSeenAt, sessionIndex }, previousLastSeenAt: session.lastSeenAt }

    if (!(await store.commit(userId, sessionChange, items))) {
      await pause(attempt)
      continue
    }

    return { recorded: items.length, sessionIndex, newSession }
  }

  throw new Error(`could not update the session of ${userId} after ${MAX_SESSION_WRITE_ATTEMPTS} attempts`)
}

const MCP_CLIENTS = [
  [/claude[\s_-]*code/i, 'claude_code'],
  [/cursor/i, 'cursor'],
  [/windsurf|codeium/i, 'windsurf']
]

// From whatever the call reveals: the OAuth client's registered name or id, and the user agent.
export const mcpClientOf = (...hints) => {
  const text = hints.filter(Boolean).join(' ')
  const match = MCP_CLIENTS.find(([pattern]) => pattern.test(text))

  return match ? match[1] : 'other'
}
